import type { Category } from "../api/category";
import type { Check } from "../api/check";
import {
  CHECK_TYPE_KEYS,
  CHECK_TYPE_LABELS,
  type CheckTypeKey,
  type PaymentPeriod,
  type Pricelist,
} from "../api/pricelist";
import type { UserInfo } from "../api/user";
import {
  formatPricelistPrice,
  getPaymentPeriodLabel,
  getPricelistPriceItems,
} from "./pricelistFormat";

export type TariffUiPeriod = PaymentPeriod;

const PAYMENT_PERIOD_MARKETING: Record<PaymentPeriod, string> = {
  month: "в месяц",
  quarter: "в квартал",
  year: "в год",
};

export interface TariffPlanCheckTypeView {
  key: CheckTypeKey;
  label: string;
  baseLimit: number;
  categoryNames: string[];
  schedule: boolean;
  report: boolean;
}

export interface TariffPlanPriceOption {
  key: PaymentPeriod;
  period: PaymentPeriod;
  buyAmount: number;
  price: number;
  amount: string;
  periodLabel: string;
  isPrimary?: boolean;
}

export interface TariffPlanView {
  id: string;
  title?: string;
  description?: string;
  prices: TariffPlanPriceOption[];
  defaultPeriod: PaymentPeriod;
  checkTypes: TariffPlanCheckTypeView[];
  isZeroTariff: boolean;
}

const formatChecksCount = (count: number): string => {
  const n = Math.abs(Math.trunc(count));
  const mod10 = n % 10;
  const mod100 = n % 100;

  if (mod100 >= 11 && mod100 <= 14) return `${n} проверок`;
  if (mod10 === 1) return `${n} проверка`;
  if (mod10 >= 2 && mod10 <= 4) return `${n} проверки`;
  return `${n} проверок`;
};

export const formatTariffChecksCount = formatChecksCount;

const resolveCategoryName = (
  categoryId: string,
  categoryById: Map<string, Category>
): string => {
  const category = categoryById.get(categoryId);
  return category?.name?.trim() || categoryId;
};

const resolveBaseLimit = (config: {
  limits?: { period?: string; count?: number; limit?: number }[];
  limit?: number;
}): number => {
  const limits = Array.isArray(config.limits) ? config.limits : [];
  const normalized = limits.flatMap((item) => {
    const count = Number(item.count);
    const legacy = Number(item.limit);
    const value = Number.isFinite(count) && count > 0 ? count : legacy;
    if (!Number.isFinite(value) || value <= 0) return [];
    return [{ period: String(item.period ?? ""), count: value }];
  });
  if (normalized.length) {
    const month = normalized.find((item) => item.period === "month");
    if (month) return month.count;
    return Math.max(...normalized.map((item) => item.count));
  }
  const legacy = Number(config.limit);
  return Number.isFinite(legacy) && legacy > 0 ? legacy : 0;
};

const buildCheckTypesFromConfig = (
  pricelist: Pricelist,
  categoryById: Map<string, Category>
): TariffPlanCheckTypeView[] => {
  if (pricelist.checkTypes) {
    return CHECK_TYPE_KEYS.flatMap((key) => {
      const config = pricelist.checkTypes?.[key];
      if (!config?.enabled) return [];
      const baseLimit = resolveBaseLimit(config);
      if (!Number.isFinite(baseLimit) || baseLimit <= 0) return [];

      return [
        {
          key,
          label: CHECK_TYPE_LABELS[key],
          baseLimit,
          categoryNames: (config.categoryIds ?? [])
            .map((id) => resolveCategoryName(id, categoryById))
            .filter(Boolean),
          schedule: config.options?.schedule === true,
          report: config.options?.report === true,
        },
      ];
    });
  }

  const legacy: TariffPlanCheckTypeView[] = [];

  const siteCategoryIds = [
    ...new Set(
      (pricelist.tariffItemList ?? [])
        .map((item) => item.categoryId)
        .filter((id): id is string => Boolean(id))
    ),
  ];
  const siteLimit = (pricelist.tariffItemList ?? []).reduce((max, item) => {
    const limit = Number(item.limit);
    if (!Number.isFinite(limit)) return max;
    return Math.max(max, limit);
  }, 0);

  if (siteLimit > 0 || siteCategoryIds.length || (pricelist.siteAmount ?? 0) > 0) {
    legacy.push({
      key: "siteCheck",
      label: CHECK_TYPE_LABELS.siteCheck,
      baseLimit: siteLimit > 0 ? siteLimit : Math.max(pricelist.siteAmount ?? 0, 1),
      categoryNames: siteCategoryIds.map((id) => resolveCategoryName(id, categoryById)),
      schedule: pricelist.scanByScheduleEnabled === true,
      report: pricelist.emailNotification === true,
    });
  }

  if (pricelist.policyCheckEnabled === true) {
    const policyLimit =
      pricelist.policyCheckSettings?.usageSettings?.reduce((max, item) => {
        const limit = Number(item.limit);
        if (!Number.isFinite(limit)) return max;
        return Math.max(max, limit);
      }, 0) ?? 0;

    legacy.push({
      key: "policyCheck",
      label: CHECK_TYPE_LABELS.policyCheck,
      baseLimit: policyLimit > 0 ? policyLimit : 1,
      categoryNames: [],
      schedule: false,
      report: pricelist.emailNotification === true,
    });
  }

  return legacy;
};

const resolveDefaultPeriod = (
  prices: TariffPlanPriceOption[],
  preferMonth: boolean
): PaymentPeriod => {
  if (preferMonth) {
    const monthly = prices.find((item) => item.key === "month");
    if (monthly) return "month";
  }
  const monthly = prices.find((item) => item.key === "month");
  if (monthly) return "month";
  return prices[0]?.key ?? "month";
};

const toPriceOption = (
  key: PaymentPeriod,
  period: PaymentPeriod,
  price: number,
  buyAmount: number,
  isPrimary: boolean
): TariffPlanPriceOption => ({
  key,
  period,
  buyAmount,
  price,
  amount: formatPricelistPrice(price),
  periodLabel: PAYMENT_PERIOD_MARKETING[key] ?? getPaymentPeriodLabel(period).toLowerCase(),
  isPrimary,
});

const buildPriceOptions = (pricelist: Pricelist): TariffPlanPriceOption[] => {
  const prices = getPricelistPriceItems(pricelist);
  const byPeriod = new Map(prices.map((item) => [item.period, item]));
  const month = byPeriod.get("month");
  const quarter = byPeriod.get("quarter");
  const year = byPeriod.get("year");
  const options: TariffPlanPriceOption[] = [];

  if (month) {
    options.push(toPriceOption("month", "month", month.price, 1, true));
  }

  if (quarter) {
    options.push(toPriceOption("quarter", "quarter", quarter.price, 1, !month));
  } else if (month) {
    options.push(toPriceOption("quarter", "month", month.price * 3, 3, false));
  }

  if (year) {
    options.push(toPriceOption("year", "year", year.price, 1, !month && !quarter));
  } else if (month) {
    options.push(toPriceOption("year", "month", month.price * 12, 12, false));
  }

  if (!options.length) {
    return prices.map((priceItem, index) =>
      toPriceOption(priceItem.period, priceItem.period, priceItem.price, 1, index === 0)
    );
  }

  return options;
};

const isVisibleForUsers = (pricelist: Pricelist): boolean =>
  pricelist.visibleForUsers !== false;

export const buildTariffPlanViews = (
  pricelists: Pricelist[],
  categories: Category[],
  _checks: Check[] = []
): TariffPlanView[] => {
  const categoryById = new Map(
    categories
      .filter((category): category is Category & { id: string } => Boolean(category.id))
      .map((category) => [category.id, category])
  );

  return pricelists
    .filter((item) => item.status === "active" && isVisibleForUsers(item))
    .flatMap((item, index) => {
      const prices = buildPriceOptions(item);
      const checkTypes = buildCheckTypesFromConfig(item, categoryById);
      if (!prices.length && !checkTypes.length) return [];

      const isZeroTariff = prices.length > 0 && prices.every((price) => price.price === 0);
      const availablePrices = isZeroTariff
        ? prices.filter((price) => price.key === "month")
        : prices;

      if (!availablePrices.length && prices.length) {
        return [];
      }

      return [
        {
          id: item.id ?? `plan-${index}`,
          title: item.name?.trim() || undefined,
          description: item.description?.trim() || undefined,
          prices: availablePrices.length ? availablePrices : prices,
          defaultPeriod: resolveDefaultPeriod(availablePrices.length ? availablePrices : prices, isZeroTariff),
          checkTypes,
          isZeroTariff,
        },
      ];
    });
};

export const usedFreeTariff = (userInfo: UserInfo | null | undefined): boolean => {
  if (!userInfo) return false;
  if (typeof userInfo.freeTariffUsed === "boolean") return userInfo.freeTariffUsed;
  const history = [
    ...(userInfo.activeTariffs ?? []),
    ...(userInfo.requestedTariffs ?? []),
    ...(Array.isArray(userInfo.archivedTariffs) ? userInfo.archivedTariffs : []),
  ];
  return history.some((item) => item.tariffPrice === 0);
};

export const MSG_FREE_ONCE =
  "Бесплатный тариф можно подключить только один раз и только на месяц";

export const MSG_FREE_MONTH = "Бесплатный тариф доступен только на месяц";
